import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, catchError, from, map, merge, mergeMap, of, tap } from 'rxjs';

import { TicketsBoard } from '@models';
import { TicketsBoardService } from '../../services/tickets-board.service';
import { DocsActions } from '../docs/docs.actions';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import { HistoryFacade } from '../history-entries';
import { TicketsActions } from './tickets.actions';
import { selectTicketsBoard } from './tickets.selectors';
import {
  KanbanColumn,
  compactChatMessages,
  normalizeBoard,
  persistExecutionModel,
  readPersistedExecutionModel,
  selectLatestBoardRecord,
} from './tickets.models';

const CONTEXT_KEYS = ['tickets.chat', 'define.definition', 'db-design.main', 'architecture.main', 'repos.settings'] as const;

async function loadBoardColumns(svc: TicketsBoardService, projectId: string): Promise<KanbanColumn[]> {
  const res = await svc.find({ where: { projectId, key: 'tickets.board' } as any });
  const existing = selectLatestBoardRecord((res.data ?? []) as TicketsBoard[]);
  return (existing?.columns ?? []) as KanbanColumn[];
}

async function saveBoardRecord(
  svc: TicketsBoardService,
  history: HistoryFacade,
  projectId: string,
  columns: KanbanColumn[]
): Promise<number> {
  const found = await svc.find({ where: { projectId, key: 'tickets.board' } as any });
  const current = selectLatestBoardRecord((found.data ?? []) as TicketsBoard[]);
  const entity = current ? current : new TicketsBoard();
  entity.projectId = projectId;
  entity.key = 'tickets.board';
  entity.columns = columns;
  entity.version = (entity.version ?? 1) + (current ? 1 : 0);
  await svc.save(entity);
  await history.add(projectId, 'tickets.board', 'Updated ticket board', 'update' as any);
  return Date.now();
}

export const loadTickets$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), svc = inject(TicketsBoardService)) =>
    actions$.pipe(
      ofType(TicketsActions.load),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([, pid]) => {
        if (!pid) return EMPTY;
        const immediate = [
          TicketsActions.loadStarted(),
          TicketsActions.setExecutionModelId({ modelId: readPersistedExecutionModel(pid) }),
          ...CONTEXT_KEYS.map((key) => DocsActions.loadDoc({ projectId: pid, key })),
        ];
        const board$ = from(loadBoardColumns(svc, pid)).pipe(
          map((columns) => TicketsActions.boardLoaded({ columns })),
          catchError((err) => of(TicketsActions.loadFailed({ error: String(err?.message ?? err) })))
        );
        return merge(from(immediate), board$);
      })
    ),
  { functional: true }
);

const refreshBoard$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), svc = inject(TicketsBoardService)) =>
    actions$.pipe(
      ofType(TicketsActions.refreshBoard),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ projectId }, activePid]) => {
        const pid = projectId ?? activePid;
        if (!pid) return EMPTY;
        return from(loadBoardColumns(svc, pid)).pipe(
          map((columns) => TicketsActions.boardLoaded({ columns })),
          catchError((err) => of(TicketsActions.loadFailed({ error: String(err?.message ?? err) })))
        );
      })
    ),
  { functional: true }
);

export const saveBoard$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), svc = inject(TicketsBoardService), history = inject(HistoryFacade)) =>
    actions$.pipe(
      ofType(TicketsActions.saveBoard),
      concatLatestFrom(() => [store.select(selectActiveProjectId), store.select(selectTicketsBoard)]),
      mergeMap(([, pid, board]) => {
        if (!pid) return EMPTY;
        const snapshot = normalizeBoard(board);
        return merge(
          of(TicketsActions.saveBoardStarted()),
          from(saveBoardRecord(svc, history, pid, snapshot)).pipe(
            map((savedAt) => TicketsActions.saveBoardSucceeded({ savedAt })),
            catchError((err) => of(TicketsActions.saveBoardFailed({ error: String(err?.message ?? err) })))
          )
        );
      })
    ),
  { functional: true }
);

export const setChatMessages$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(TicketsActions.setChatMessages),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ messages }, pid]) => {
        if (!pid) return EMPTY;
        const data = { messages: compactChatMessages(messages) };
        return [
          DocsActions.applyLocalData({ projectId: pid, key: 'tickets.chat', data }),
          DocsActions.saveDoc({ projectId: pid, key: 'tickets.chat', data }),
        ];
      })
    ),
  { functional: true }
);

export const persistExecutionModel$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(TicketsActions.setExecutionModelId),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      tap(([{ modelId }, pid]) => persistExecutionModel(modelId, pid))
    ),
  { functional: true, dispatch: false }
);

export const ticketsEffects = { loadTickets$, refreshBoard$, saveBoard$, setChatMessages$, persistExecutionModel$ };
